import React from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'shared/components';
import { AITextareaField } from './AITextareaField';
import { ProposalAIPrimaryButton } from './ProposalAIPrimaryButton';
import { ProposalAIRichContent } from './ProposalAIRichContent';

interface Props {
  index: number;
  prompt: string;
  generated: GenerateServiceSuggestionPayload | null;
  isGenerating: boolean;
  isGeneratedStale: boolean;
  onPromptChange: (value: string) => void;
  onGenerate: React.MouseEventHandler<HTMLButtonElement>;
  onApply: React.MouseEventHandler<HTMLButtonElement>;
  onCancel: React.MouseEventHandler<HTMLButtonElement>;
}

export const ServiceSegmentAIPanel: React.FC<Props> = ({
  index,
  prompt,
  generated,
  isGenerating,
  isGeneratedStale,
  onPromptChange,
  onGenerate,
  onApply,
  onCancel,
}) => {
  const { formatMessage } = useIntl();

  return (
    <div className="proposal-service-ai__panel">
      <div className="proposal-service-ai__header">
        <p className="t-label m-bottom--tny">
          {formatMessage({ id: 'proposal-ai.service.eyebrow' })}
        </p>
        <p className="t-text-2 m-bottom-0">
          {formatMessage({ id: 'proposal-ai.service.description' })}
        </p>
      </div>

      <AITextareaField
        id={`proposal-ai-service-prompt-${index}`}
        label={formatMessage({ id: 'proposal-ai.service.prompt.label' })}
        placeholder={formatMessage({
          id: 'proposal-ai.service.prompt.placeholder',
        })}
        value={prompt}
        onChange={onPromptChange}
        dataCy={`proposal-ai-service-prompt-${index}`}
        textareaClassName="proposal-service-ai__prompt"
      />

      <div className="proposal-ai-generator__action-row">
        <ProposalAIPrimaryButton
          label={
            isGenerating
              ? formatMessage({ id: 'proposal-ai.service.generate.loading' })
              : generated
                ? formatMessage({
                    id: 'proposal-ai.service.generate.regenerate',
                  })
                : formatMessage({ id: 'proposal-ai.service.generate.action' })
          }
          onClick={onGenerate}
          disabled={isGenerating}
          btnSelector="proposal-ai-service-generate"
          size="med"
        />
        <Button
          style="ghost"
          size="med"
          onClick={onCancel}
          btnSelector="proposal-ai-service-cancel"
        >
          {formatMessage({ id: 'buttons.cancel' })}
        </Button>
      </div>

      {generated && (
        <>
          {isGeneratedStale && (
            <div className="proposal-ai-generator__notice proposal-ai-generator__notice--warning">
              <p className="t-text-2 m-bottom-0">
                {formatMessage({ id: 'proposal-ai.service.preview.stale' })}
              </p>
            </div>
          )}

          <div className="proposal-service-ai__preview">
            <p className="t-label m-bottom--tny">
              {formatMessage({ id: 'proposal-ai.service.preview.title' })}
            </p>
            <ProposalAIRichContent content={generated.description} />
          </div>

          <div className="proposal-ai-generator__action-row">
            <ProposalAIPrimaryButton
              label={formatMessage({ id: 'proposal-ai.service.apply' })}
              onClick={onApply}
              disabled={isGeneratedStale}
              btnSelector="proposal-ai-service-apply"
              size="med"
            />
          </div>
        </>
      )}
    </div>
  );
};
